import { Switch, Route } from "wouter";
import { queryClient, setEntitlementErrorHandler } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, Component, useState, useEffect } from "react";
import type { ReactNode } from "react";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/Dashboard";
import { SyncProvider } from "@/contexts/SyncContext";
import { Sentry, setSentryUser } from "./lib/sentry";
import { UpgradeModal, registerUpgradeModal } from "@/components/UpgradeModal";

const StudentView = lazy(() => import("@/pages/StudentView"));
const ParentView = lazy(() => import("@/pages/ParentView"));
const AdminView = lazy(() => import("@/pages/AdminView"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Pricing = lazy(() => import("@/pages/Pricing"));

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[Nova ErrorBoundary]", error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-6">
          <div className="max-w-md text-center space-y-4">
            <div className="text-4xl">😵</div>
            <h2 className="text-xl font-bold text-slate-800">Something went wrong</h2>
            <p className="text-sm text-slate-500">{this.state.error?.message || "An unexpected error occurred."}</p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.history.length > 1 ? window.history.back() : (window.location.href = "/"); }}
              className="px-6 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
              data-testid="button-error-go-back"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
      <div className="w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Router() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Switch>
          <Route path="/" component={Dashboard}/>
          <Route path="/student" component={StudentView}/>
          <Route path="/parent" component={ParentView}/>
          <Route path="/admin" component={AdminView}/>
          <Route path="/reset-password" component={ResetPassword}/>
          <Route path="/privacy" component={PrivacyPolicy}/>
          <Route path="/terms" component={TermsOfService}/>
          <Route path="/pricing" component={Pricing}/>
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

function UpgradeModalProvider() {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("");
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [upgradePlan, setUpgradePlan] = useState("free");

  useEffect(() => {
    registerUpgradeModal({
      setOpen: setUpgradeOpen,
      setFeature: setUpgradeFeature,
      setMessage: setUpgradeMessage,
      setPlan: setUpgradePlan,
    });

    setEntitlementErrorHandler(({ message, plan }) => {
      setUpgradeMessage(message);
      setUpgradePlan(plan || "free");
      setUpgradeOpen(true);
    });

    return () => {
      registerUpgradeModal(null);
      setEntitlementErrorHandler(() => {});
    };
  }, []);

  return (
    <UpgradeModal
      open={upgradeOpen}
      onOpenChange={setUpgradeOpen}
      feature={upgradeFeature}
      message={upgradeMessage}
      currentPlan={upgradePlan}
    />
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(r => {
        if (r.ok) {
          setIsAuthenticated(true);
          return r.json();
        }
        return null;
      })
      .then(data => {
        if (data?.id) {
          setSentryUser({ parentId: data.id });
        }
      })
      .catch(() => {});

    const handleAuth = () => setIsAuthenticated(true);
    const handleLogout = () => {
      setIsAuthenticated(false);
      setSentryUser({});
    };
    window.addEventListener("nova:auth", handleAuth);
    window.addEventListener("nova:logout", handleLogout);
    return () => {
      window.removeEventListener("nova:auth", handleAuth);
      window.removeEventListener("nova:logout", handleLogout);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <SyncProvider isAuthenticated={isAuthenticated}>
        <TooltipProvider>
          <Toaster />
          <UpgradeModalProvider />
          <Router />
        </TooltipProvider>
      </SyncProvider>
    </QueryClientProvider>
  );
}

export default App;
